import React, { useMemo } from 'react';
import { GivelifyErrorBanner, GivelifyLabel } from '@givelify/givelify-ui';
import { GivelifyAvatar, GivelifyNotification } from '@givelify/ui';
import { useTranslation } from 'react-i18next';
import { EditUserInfo } from 'types/userTypes';
import {
    StyledModal,
    ModalContainer,
    Statement,
    Description,
    ErrorBanner,
    UsernameContainer,
    UserAvatarWrapper,
    UserNameContent,
    FullName,
    Title,
    ModalFooter,
    StyledButton,
} from './ConfirmationModalStyle';

interface DeleteUserModalProps {
    onDeleteUserModalClose: () => void;
    onDelete: () => void;
    userInfo: EditUserInfo;
    userAvatar: string;
    deleteError?: string;
    isDeleting?: boolean;
}

const DeleteUserModal: React.FCC<DeleteUserModalProps> = (
    props: DeleteUserModalProps,
) => {
    const { userInfo, userAvatar } = props;
    const fullName = `${userInfo.firstName} ${userInfo.lastName}`;
    const { t } = useTranslation();
    const copy = useMemo(
        () => ({
            deleteUser: t('pages.settings.users.delete-user'),
            deleteConfirm: t(
                'pages.settings.users.delete-modal.delete-confirm',
            ),
            deleteConfirmTip: t(
                'pages.settings.users.delete-modal.delete-confirm-tip',
            ),
            deleteConfirmDesc: t(
                'pages.settings.users.delete-modal.delete-confirm-desc',
            ),
            cancel: t('labels.cancel'),
            delete: t('pages.settings.users.delete-modal.delete'),
        }),
        [t],
    );
    return (
        <StyledModal
            autoFullscreen
            open
            showCloseButton
            contentClassName="confirmation-modal"
            name="delete-user-modal"
            onClose={props.onDeleteUserModalClose}
            size="small"
        >
            <ModalContainer>
                <Statement
                    marginTop={32}
                    text={copy.deleteConfirm}
                    variant="heading3"
                />
                <Description
                    marginTop={16}
                    text={copy.deleteConfirmDesc}
                    variant="body1"
                />
                {props.deleteError ? (
                    <ErrorBanner>
                        <GivelifyErrorBanner
                            marginTop={32}
                            text={props.deleteError}
                        />
                    </ErrorBanner>
                ) : null}
                <UsernameContainer aria-label="User">
                    <UserAvatarWrapper>
                        <GivelifyAvatar
                            color="grey"
                            size="medium"
                            src={userAvatar}
                        />
                    </UserAvatarWrapper>
                    <UserNameContent>
                        <FullName>
                            <GivelifyLabel
                                bold
                                text={fullName}
                                variant="heading5"
                            />
                        </FullName>
                        <Title text={userInfo.title} variant="small" />
                    </UserNameContent>
                </UsernameContainer>
                <GivelifyNotification
                    open
                    text={copy.deleteConfirmTip}
                    variant="warning"
                />
                <ModalFooter>
                    <StyledButton
                        disabled={props.isDeleting}
                        onClick={props.onDeleteUserModalClose}
                        size="large"
                        sx={{ mr: 1 }}
                        variant="outlined"
                    >
                        {copy.cancel}
                    </StyledButton>
                    <StyledButton
                        $isError
                        disabled={props.isDeleting}
                        onClick={props.onDelete}
                        size="large"
                        sx={{ ml: 1 }}
                        variant="contained"
                    >
                        {copy.delete}
                    </StyledButton>
                </ModalFooter>
            </ModalContainer>
        </StyledModal>
    );
};

export default DeleteUserModal;
